// Static-phase grouping, sorting and formatting for the Location Detail table. At integration the backend groups, sorts and pages; this file is then removed.
import {
  formatCount,
  formatPercent,
  ratio,
  stagedFor,
  sumRows,
  type WesStaticRow,
  type WesSums,
} from './wesPalletStagingStaticRows';

export type WesDimKey = 'region' | 'market' | 'location' | 'locationId' | 'year' | 'period' | 'week';
export type WesMetricKey = 'total' | 'staged' | 'other' | 'audit';
export type WesDisplayMode = 'percent' | 'pallets';

export interface WesDimensionDef {
  key: WesDimKey;
  label: string;
  group: 'geography' | 'timeframe';
  text: (row: WesStaticRow) => string;
  sortValue: (row: WesStaticRow) => string | number;
}

export const WES_DIMENSIONS: WesDimensionDef[] = [
  { key: 'region', label: 'Region / Unit', group: 'geography', text: (r) => r.regionName, sortValue: (r) => r.regionName },
  { key: 'market', label: 'Market / POD', group: 'geography', text: (r) => r.marketName, sortValue: (r) => r.marketName },
  { key: 'location', label: 'Location', group: 'geography', text: (r) => r.locationName, sortValue: (r) => r.locationName },
  { key: 'locationId', label: 'Location ID', group: 'geography', text: (r) => r.locationId, sortValue: (r) => r.locationId },
  { key: 'year', label: 'Year', group: 'timeframe', text: (r) => String(r.year), sortValue: (r) => r.year },
  { key: 'period', label: 'Period', group: 'timeframe', text: (r) => `P${r.period}`, sortValue: (r) => r.period },
  { key: 'week', label: 'Week in Year', group: 'timeframe', text: (r) => `Week ${r.week}`, sortValue: (r) => r.week },
];

export const WES_METRICS: { key: WesMetricKey; label: string }[] = [
  { key: 'total', label: 'Total Pallets' },
  { key: 'staged', label: 'Total Staged Pallets' },
  { key: 'other', label: 'Staged Pallets (Other)' },
  { key: 'audit', label: 'Staged Pallets (Audit-Related)' },
];

export const WES_DEFAULT_DIMENSIONS: WesDimKey[] = ['location'];
export const WES_PAGE_SIZE = 25;
export const WES_PAGE_SIZE_OPTIONS = [25, 50, 100];
export const WES_TABLE_EMPTY_MESSAGE = 'No records match the current filters';

export interface WesTableSort {
  key: string;
  dir: 'asc' | 'desc';
}

// Default order when no sort is chosen, or the chosen column is hidden.
export const WES_DEFAULT_SORT: WesTableSort = { key: 'location', dir: 'asc' };

export const isDimensionKey = (key: string): key is WesDimKey => WES_DIMENSIONS.some((d) => d.key === key);

export interface WesGroupedRow {
  id: string;
  text: Partial<Record<WesDimKey, string>>;
  sortValues: Partial<Record<WesDimKey, string | number>>;
  sums: WesSums;
}

export const groupRows = (rows: WesStaticRow[], dims: WesDimKey[]): WesGroupedRow[] => {
  const defs = WES_DIMENSIONS.filter((d) => dims.includes(d.key));
  const buckets = new Map<string, WesStaticRow[]>();
  rows.forEach((r) => {
    // Location groups by its ID so locations that share a display name never merge.
    const key = defs.map((d) => (d.key === 'location' ? r.locationId : d.text(r))).join('|');
    buckets.set(key, [...(buckets.get(key) ?? []), r]);
  });
  return [...buckets.entries()].map(([id, bucketRows]) => {
    const first = bucketRows[0];
    const text: WesGroupedRow['text'] = {};
    const sortValues: WesGroupedRow['sortValues'] = {};
    defs.forEach((d) => {
      text[d.key] = d.text(first);
      sortValues[d.key] = d.sortValue(first);
    });
    return { id, text, sortValues, sums: sumRows(bucketRows) };
  });
};

export const metricCount = (sums: WesSums, key: WesMetricKey): number =>
  key === 'total' ? sums.total : key === 'staged' ? stagedFor(sums, 'all') : key === 'other' ? sums.other : sums.audit;

// Total Pallets is always a count; staged metrics follow the display mode.
export const formatMetric = (sums: WesSums, key: WesMetricKey, mode: WesDisplayMode): string => {
  const count = metricCount(sums, key);
  return key === 'total' || mode === 'pallets' ? formatCount(count) : formatPercent(count, sums.total);
};

// Unrounded sort value: a ratio in Percent mode, so sorting never depends on display rounding.
const metricSortValue = (sums: WesSums, key: WesMetricKey, mode: WesDisplayMode): number => {
  const count = metricCount(sums, key);
  if (key === 'total' || mode === 'pallets') return count;
  return ratio(count, sums.total) ?? Number.NEGATIVE_INFINITY;
};

export const effectiveSort = (sort: WesTableSort | null, dims: WesDimKey[]): WesTableSort => {
  const defaultSort: WesTableSort = { key: WES_DIMENSIONS.find((dimension) => dims.includes(dimension.key))?.key ?? WES_DEFAULT_SORT.key, dir: 'asc' };
  if (!sort) return defaultSort;
  const visible = isDimensionKey(sort.key) ? dims.includes(sort.key) : WES_METRICS.some((m) => m.key === sort.key);
  return visible ? sort : defaultSort;
};

export const sortGroups = (
  groups: WesGroupedRow[],
  dims: WesDimKey[],
  mode: WesDisplayMode,
  sort: WesTableSort | null,
): WesGroupedRow[] => {
  const active = effectiveSort(sort, dims);
  const factor = active.dir === 'asc' ? 1 : -1;
  const value = (g: WesGroupedRow): string | number =>
    isDimensionKey(active.key)
      ? g.sortValues[active.key] ?? ''
      : metricSortValue(g.sums, active.key as WesMetricKey, mode);
  return [...groups].sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    if (typeof av === 'number' && typeof bv === 'number') return (av === bv ? 0 : av < bv ? -1 : 1) * factor;
    return String(av).localeCompare(String(bv)) * factor;
  });
};

// asc -> desc -> default (null)
export const nextSort = (current: WesTableSort | null, key: string): WesTableSort | null => {
  if (!current || current.key !== key) return { key, dir: 'asc' };
  return current.dir === 'asc' ? { key, dir: 'desc' } : null;
};
